import { useState, type FormEvent, type ReactNode } from 'react';
import { Modal } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { userService } from '@/api/admin/services/user.service';
import { getApiErrorMessage } from '@/utils/dashboard/apiError';
import type { Vendor } from '@/types/dashboard/vendor';
import { useCreateVendor, useUpdateVendor } from './useVendors';

// GUESS: confirm the exact role code in your backend (Access Control > Roles).
const VENDOR_ROLE = 'VENDOR';

interface FormValues {
  name: string;
  contact_person: string;
  email: string;
  phone: string;
  address: string;
  is_active: boolean;
  password: string;
}
type FormErrors = Partial<Record<keyof FormValues, string>>;

interface Props {
  vendor: Vendor | null; // null = onboard new vendor
  merchantId: number | null;
  onClose: () => void;
}

function validate(v: FormValues, isEdit: boolean): FormErrors {
  const e: FormErrors = {};
  if (!v.name.trim()) e.name = 'Vendor name is required';
  if (!v.contact_person.trim()) e.contact_person = 'Contact person is required';
  if (!/^\S+@\S+\.\S+$/.test(v.email.trim())) e.email = 'Enter a valid email address';
  if (!/^[0-9+\-\s]{7,15}$/.test(v.phone.trim())) e.phone = 'Enter a valid phone number';
  if (!isEdit && v.password.length < 8) e.password = 'Password must be at least 8 characters';
  return e;
}

function Field({ label, error, children }: { label: string; error?: string; children: ReactNode }) {
  return (
    <div className="mb-3">
      <label className="text-label-md text-on-surface mb-1 d-block">{label}</label>
      {children}
      {error && <div className="text-danger text-body-sm mt-1">{error}</div>}
    </div>
  );
}

export function VendorFormModal({ vendor, merchantId, onClose }: Props) {
  const isEdit = vendor !== null;
  const createVendor = useCreateVendor();
  const updateVendor = useUpdateVendor();

  const [values, setValues] = useState<FormValues>({
    name: vendor?.name ?? '',
    contact_person: vendor?.contact_person ?? '',
    email: vendor?.email ?? '',
    phone: vendor?.phone ?? '',
    address: vendor?.address ?? '',
    is_active: vendor?.is_active ?? true,
    password: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Kept across retries so a failed vendor save never creates a second login user
  const [createdUserId, setCreatedUserId] = useState<number | null>(null);

  const set = <K extends keyof FormValues>(key: K, val: FormValues[K]) =>
    setValues((p) => ({ ...p, [key]: val }));

  const invalid = (k: keyof FormValues) => (errors[k] ? ' is-invalid' : '');

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const errs = validate(values, isEdit);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitError('');
    setSubmitting(true);
    let userId = createdUserId;

    try {
      const base = {
        name: values.name.trim(),
        contact_person: values.contact_person.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        address: values.address.trim(),
        is_active: values.is_active,
      };

      if (vendor) {
        await updateVendor.mutateAsync({ id: vendor.id, payload: base });
        toast.success('Vendor updated');
      } else {
        if (merchantId === null) {
          setSubmitError('Your account has no merchant, so vendors cannot be created.');
          return;
        }
        if (userId === null) {
          // Vendor email doubles as the login username
          const user = await userService.register({
            username: base.email,
            password: values.password,
            full_name: base.contact_person,
            user_type: 'VENDOR',
            role: VENDOR_ROLE,
          });
          userId = user.id;
          setCreatedUserId(user.id);
        }
        await createVendor.mutateAsync({ ...base, user: userId, merchant: merchantId });
        toast.success('Vendor onboarded');
      }
      onClose();
    } catch (err) {
      const msg = getApiErrorMessage(err);
      setSubmitError(
        userId !== null && !isEdit
          ? `${msg}. The login account was created; press Save again to retry the vendor record.`
          : msg,
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show onHide={submitting ? undefined : onClose} centered size="lg" backdrop="static">
      <form onSubmit={onSubmit} noValidate>
        <Modal.Header closeButton={!submitting}>
          <Modal.Title className="text-headline-sm">
            {isEdit ? 'Edit vendor' : 'Onboard vendor'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="row">
            <div className="col-md-6">
              <Field label="Vendor name" error={errors.name}>
                <input
                  className={`form-control${invalid('name')}`}
                  value={values.name}
                  onChange={(e) => set('name', e.target.value)}
                  autoFocus
                />
              </Field>
            </div>
            <div className="col-md-6">
              <Field label="Contact person" error={errors.contact_person}>
                <input
                  className={`form-control${invalid('contact_person')}`}
                  value={values.contact_person}
                  onChange={(e) => set('contact_person', e.target.value)}
                />
              </Field>
            </div>
            <div className="col-md-6">
              <Field label="Email" error={errors.email}>
                <input
                  type="email"
                  className={`form-control${invalid('email')}`}
                  value={values.email}
                  onChange={(e) => set('email', e.target.value)}
                  disabled={createdUserId !== null}
                />
              </Field>
            </div>
            <div className="col-md-6">
              <Field label="Phone" error={errors.phone}>
                <input
                  className={`form-control${invalid('phone')}`}
                  value={values.phone}
                  onChange={(e) => set('phone', e.target.value)}
                  inputMode="tel"
                />
              </Field>
            </div>
          </div>

          <Field label="Address">
            <textarea
              className="form-control"
              rows={2}
              value={values.address}
              onChange={(e) => set('address', e.target.value)}
            />
          </Field>

          {!isEdit && (
            <div className="bg-surface-low rounded-3 p-3 mb-3">
              <div className="text-label-md text-on-surface mb-1">Login account</div>
              <p className="text-body-sm text-on-surface-variant mb-3">
                The vendor signs in with their email above and this password.
              </p>
              <Field label="Password" error={errors.password}>
                <input
                  type="password"
                  className={`form-control${invalid('password')}`}
                  value={values.password}
                  onChange={(e) => set('password', e.target.value)}
                  autoComplete="new-password"
                  disabled={createdUserId !== null}
                />
              </Field>
            </div>
          )}

          <div className="form-check form-switch">
            <input
              id="vendor-active"
              type="checkbox"
              className="form-check-input"
              checked={values.is_active}
              onChange={(e) => set('is_active', e.target.checked)}
            />
            <label className="form-check-label text-label-md" htmlFor="vendor-active">
              Active
            </label>
          </div>

          {submitError && (
            <div className="alert alert-danger d-flex gap-2 mt-3 mb-0 py-2" role="alert">
              <i className="bi bi-exclamation-circle" />
              <span className="text-body-sm">{submitError}</span>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Saving…
              </>
            ) : isEdit ? (
              'Save changes'
            ) : (
              'Onboard vendor'
            )}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}